import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../supabase';
import { getMe } from '../api/auth';
import { ApiError } from '../api/client';
import type { AuthMeResponse } from '../api/types';

export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated' | 'orphan';

export interface AuthContextValue {
  status: AuthStatus;
  me: AuthMeResponse | null;
  error: string | null;
  /**
   * ISO-8601 del último sign-in del usuario según Supabase
   * (`session.user.last_sign_in_at`). Se usa en el banner del Layout
   * (S11-01). Null si no hay sesión o el SDK no provee el dato.
   */
  lastSignInAt: string | null;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [me, setMe] = useState<AuthMeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastSignInAt, setLastSignInAt] = useState<string | null>(null);

  const loadMe = useCallback(async () => {
    try {
      const profile = await getMe();
      setMe(profile);
      setError(null);
      setStatus('authenticated');
    } catch (err) {
      // 404 en /auth/me = usuario en auth.users de Supabase pero sin row en
      // backend.usuarios (cuenta huérfana). Caso raro pero posible si alguien
      // disparó un signup directo. Salir y pedir contactar a la comisión.
      if (err instanceof ApiError && err.status === 404) {
        await supabase.auth.signOut();
        setMe(null);
        setError('Tu cuenta no está completa. Contacta a la comisión.');
        setStatus('orphan');
        return;
      }
      setMe(null);
      setError(err instanceof Error ? err.message : 'Error al cargar perfil');
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!active) return;
      if (session) {
        setLastSignInAt(session.user.last_sign_in_at ?? null);
        loadMe();
      } else {
        setStatus('unauthenticated');
      }
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      // INITIAL_SESSION dispara cuando Supabase termina de procesar el hash
      // del Magic Link al cargar /auth/callback. Sin este evento, el callback
      // se queda en `unauthenticated` aunque la sesion sí se cargo.
      if (
        event === 'SIGNED_IN' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'INITIAL_SESSION'
      ) {
        if (session) {
          setLastSignInAt(session.user.last_sign_in_at ?? null);
          loadMe();
        } else if (event === 'INITIAL_SESSION') {
          setStatus('unauthenticated');
        }
      } else if (event === 'SIGNED_OUT') {
        setMe(null);
        setError(null);
        setLastSignInAt(null);
        setStatus('unauthenticated');
      }
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadMe]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setMe(null);
    setError(null);
    setLastSignInAt(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, error, lastSignInAt, signOut, refreshMe: loadMe }),
    [status, me, error, lastSignInAt, signOut, loadMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return ctx;
}

export { AuthContext };
